import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { useResourceLimits } from "@/hooks/useResourceLimits";
import { toast } from "@/components/ui/sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getInvokeErrorText, isGoogleCalendarReconnectError } from "@/lib/invokeError";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";

export type GoogleCalendarDialogMode = "manage" | "pick-one";

interface GoogleCalendarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** manage = multi-select CRUD; pick-one = seleção única (legado). Default implícito = legado. */
  mode?: GoogleCalendarDialogMode;
  credentialId?: string;
  onSaved?: () => void;
}

interface CalendarInfo {
  id: string;
  summary: string;
  primary?: boolean;
}

interface SyncCalendarRow {
  calendar_id: string;
  is_enabled: boolean;
}

export const GoogleCalendarDialog = ({
  open,
  onOpenChange,
  mode,
  credentialId: propCredentialId,
  onSaved,
}: GoogleCalendarDialogProps) => {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { checkCanCreate } = useResourceLimits(workspaceId);
  const multiUiEnabled = isGcalMultiCalendarEnabled() && mode === "manage";

  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [activeCredentialId, setActiveCredentialId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [calendars, setCalendars] = useState<CalendarInfo[]>([]);
  const [selectedCalendar, setSelectedCalendar] = useState<string>("primary");
  const [selectedCalendars, setSelectedCalendars] = useState<Set<string>>(new Set());
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false);
  const [currentCalendarId, setCurrentCalendarId] = useState<string>("primary");
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [calendarLimit, setCalendarLimit] = useState<{ current: number; limit: number } | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const connectionStartTimeRef = useRef<string | null>(null);
  const checkConnectionRef = useRef<(showLoading?: boolean) => Promise<void>>(async () => {});
  const bootstrapCalendarSyncRef = useRef<() => Promise<void>>(async () => {});

  const loadCalendarLimit = useCallback(async () => {
    if (!multiUiEnabled) return;
    const check = await checkCanCreate("calendars");
    setCalendarLimit({ current: check.current, limit: check.limit });
  }, [checkCanCreate, multiUiEnabled]);

  const loadSyncCalendars = useCallback(async (credId: string) => {
    if (!workspaceId) return [];
    const { data } = await supabase
      .from("google_calendar_sync_calendars")
      .select("calendar_id, is_enabled")
      .eq("workspace_id", workspaceId)
      .eq("credential_id", credId);
    return (data ?? []) as SyncCalendarRow[];
  }, [workspaceId]);

  const applySyncSelection = useCallback((rows: SyncCalendarRow[], googleCals: CalendarInfo[]) => {
    const enabled = new Set(rows.filter((r) => r.is_enabled).map((r) => r.calendar_id));
    if (enabled.size === 0 && googleCals.length > 0) {
      const primary = googleCals.find((c) => c.primary)?.id ?? googleCals[0]?.id ?? "primary";
      enabled.add(primary);
    }
    setSelectedCalendars(enabled);
  }, []);

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "google-calendar-connected" && event.data?.success) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        setIsConnected(true);
        setConnectedEmail(event.data.email);
        setIsConnecting(false);
        setTokenInvalid(false);

        toast.success(`Google Calendar conectado: ${event.data.email}`);

        void checkConnectionRef.current(false);
        void bootstrapCalendarSyncRef.current();
      } else if (event.data?.type === "google-calendar-error") {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsConnecting(false);
        toast.error(`Erro: ${event.data.error}`);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const loadCalendars = useCallback(async (credentialIdOverride?: string) => {
    if (!workspaceId) return;

    const credId = credentialIdOverride || propCredentialId || activeCredentialId;

    setIsLoadingCalendars(true);
    try {
      const { data, error } = await supabase.functions.invoke("google-calendar-tools", {
        body: {
          action: "list_calendars",
          workspaceId,
          params: credId ? { credentialId: credId } : undefined,
        },
      });

      if (error) {
        const errorMessage = getInvokeErrorText(error);
        if (isGoogleCalendarReconnectError(errorMessage)) {
          setTokenInvalid(true);
          toast.warning("🗓️ Google Calendar desconectado", {
            description: "O token de acesso expirou. Clique em Reconectar para renovar.",
          });
        }
        return;
      }

      if (data?.calendars) {
        setCalendars(data.calendars);
        setTokenInvalid(false);
        if (multiUiEnabled && credId) {
          const syncRows = await loadSyncCalendars(credId);
          applySyncSelection(syncRows, data.calendars);
        }
      } else if (data?.error) {
        const errorMessage = String(data.error);
        if (isGoogleCalendarReconnectError(errorMessage)) {
          setTokenInvalid(true);
          toast.warning("🗓️ Google Calendar desconectado", {
            description: "O token de acesso expirou. Clique em Reconectar para renovar.",
          });
        }
      }
    } catch (error: unknown) {
      const errorMessage = getInvokeErrorText(error);
      if (isGoogleCalendarReconnectError(errorMessage)) {
        setTokenInvalid(true);
        toast.warning("🗓️ Google Calendar desconectado", {
          description: "O token de acesso expirou. Clique em Reconectar para renovar.",
        });
      }
    } finally {
      setIsLoadingCalendars(false);
    }
  }, [
    workspaceId,
    propCredentialId,
    activeCredentialId,
    multiUiEnabled,
    loadSyncCalendars,
    applySyncSelection,
  ]);

  const checkConnection = useCallback(async (showLoading = true) => {
    if (!workspaceId) return;

    if (showLoading) setIsLoading(true);
    setTokenInvalid(false);

    try {
      let credQuery = supabase
        .from("google_calendar_credentials")
        .select("id, email, is_active, calendar_id, needs_reconnect")
        .eq("workspace_id", workspaceId)
        .eq("is_active", true);

      if (propCredentialId) {
        credQuery = credQuery.eq("id", propCredentialId);
      }

      const { data, error } = multiUiEnabled
        ? await credQuery.order("created_at", { ascending: true }).limit(1).maybeSingle()
        : await credQuery.maybeSingle();

      if (error || !data) {
        setIsConnected(false);
        setConnectedEmail(null);
        setActiveCredentialId(null);
        return;
      }

      setIsConnected(true);
      setConnectedEmail(data.email);
      setActiveCredentialId(data.id);
      setCurrentCalendarId(data.calendar_id || "primary");
      setSelectedCalendar(data.calendar_id || "primary");
      setTokenInvalid(data.needs_reconnect === true);

      await loadCalendars(data.id);
      if (multiUiEnabled) {
        await loadCalendarLimit();
      }
    } catch (error) {
      console.error("Error checking calendar connection:", error);
      setIsConnected(false);
      setConnectedEmail(null);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [
    workspaceId,
    propCredentialId,
    multiUiEnabled,
    loadCalendars,
    loadCalendarLimit,
  ]);

  checkConnectionRef.current = checkConnection;

  useEffect(() => {
    if (open && workspaceId) {
      void checkConnectionRef.current();
    } else if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [open, workspaceId, propCredentialId]);

  const bootstrapCalendarSync = useCallback(async () => {
    if (!workspaceId) return;
    try {
      toast.info("Sincronizando Google Calendar em segundo plano...");
      await supabase.functions.invoke("google-calendar-sync", {
        body: { action: "daily_full_sync", workspaceId, reason: "connect" },
      });
      await supabase.functions.invoke("google-calendar-sync", {
        body: { action: "register_watch", workspaceId, reason: "connect" },
      });
    } catch (error) {
      console.warn("Google Calendar bootstrap sync skipped:", error);
    }
  }, [workspaceId]);

  bootstrapCalendarSyncRef.current = bootstrapCalendarSync;

  const handleCalendarChange = async (calendarId: string) => {
    if (!workspaceId || !connectedEmail) return;

    setIsLoading(true);
    try {
      const { data: cred } = await supabase
        .from("google_calendar_credentials")
        .select("id")
        .eq("workspace_id", workspaceId)
        .eq("email", connectedEmail)
        .eq("is_active", true)
        .maybeSingle();

      if (!cred?.id) {
        toast.error("Credencial do Google Calendar não encontrada");
        return;
      }

      // Fase 6: não escrever credentials.calendar_id — sync_selection + trigger DB espelha primária
      const selections = calendars.map((cal) => ({
        calendarId: cal.id,
        displayName: cal.summary,
        enabled: cal.id === calendarId,
      }));

      const { data, error } = await supabase.functions.invoke("google-calendar-tools", {
        body: {
          action: "manage_sync_calendars",
          workspaceId,
          params: {
            operation: "sync_selection",
            credentialId: cred.id,
            selections,
          },
        },
      });

      if (error) {
        toast.error(getInvokeErrorText(error) || "Erro ao atualizar agenda");
        return;
      }

      if (data?.error === "google_calendar_limit_exceeded") {
        toast.error(`Limite do plano: ${data.limit} agenda(s)`);
        return;
      }

      setCurrentCalendarId(calendarId);
      setSelectedCalendar(calendarId);
      toast.success("Agenda atualizada com sucesso");
      void bootstrapCalendarSync();
      onSaved?.();

      setTimeout(() => onOpenChange(false), 1000);
    } catch (error) {
      console.error("Error updating calendar:", error);
      toast.error("Erro ao atualizar agenda");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiSave = async () => {
    if (!workspaceId || !activeCredentialId) return;

    const enabledCount = selectedCalendars.size;
    if (enabledCount === 0) {
      toast.error("Selecione pelo menos uma agenda");
      return;
    }

    const { data: allEnabledRows } = await supabase
      .from("google_calendar_sync_calendars")
      .select("calendar_id")
      .eq("workspace_id", workspaceId)
      .eq("is_enabled", true);

    const googleCalIds = new Set(calendars.map((cal) => cal.id));
    const otherEnabledCount = (allEnabledRows ?? []).filter(
      (row) => !googleCalIds.has(row.calendar_id),
    ).length;
    const finalEnabledCount = otherEnabledCount + enabledCount;

    const check = await checkCanCreate("calendars");
    const maxAllowed = calendarLimit?.limit ?? check.limit;
    if (finalEnabledCount > maxAllowed) {
      toast.error(`Seu plano permite no máximo ${maxAllowed} agenda(s).`);
      return;
    }

    setIsSaving(true);
    try {
      const selections = calendars.map((cal) => ({
        calendarId: cal.id,
        displayName: cal.summary,
        enabled: selectedCalendars.has(cal.id),
      }));

      const { data, error } = await supabase.functions.invoke("google-calendar-tools", {
        body: {
          action: "manage_sync_calendars",
          workspaceId,
          params: {
            operation: "sync_selection",
            credentialId: activeCredentialId,
            selections,
          },
        },
      });

      if (error) {
        toast.error(getInvokeErrorText(error) || "Erro ao salvar agendas");
        return;
      }

      if (data?.error === "google_calendar_limit_exceeded") {
        toast.error(`Limite do plano: ${data.limit} agenda(s)`);
        return;
      }

      toast.success("Agendas atualizadas com sucesso");
      void bootstrapCalendarSync();
      await loadCalendarLimit();
      onSaved?.();
      setTimeout(() => onOpenChange(false), 800);
    } catch (error) {
      console.error("Error saving sync calendars:", error);
      toast.error("Erro ao salvar agendas");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCalendarSelection = (calendarId: string, checked: boolean) => {
    setSelectedCalendars((prev) => {
      const next = new Set(prev);
      if (checked) next.add(calendarId);
      else next.delete(calendarId);
      return next;
    });
  };

  const handleConnect = async () => {
    if (!workspaceId) {
      toast.error("Workspace não encontrado");
      return;
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    connectionStartTimeRef.current = new Date().toISOString();
    setIsConnecting(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        `google-calendar-oauth?workspace_id=${workspaceId}`,
        { method: "GET" },
      );

      if (error) {
        toast.error("Erro ao conectar com Google Calendar");
        setIsConnecting(false);
        return;
      }

      if (data?.authUrl) {
        window.open(data.authUrl, "_blank", "width=600,height=700");
        toast.info("Complete a autorização na janela do Google");

        let pollCount = 0;
        const maxPolls = 60;

        pollIntervalRef.current = setInterval(async () => {
          pollCount++;

          if (pollCount >= maxPolls) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setIsConnecting(false);
            await checkConnection(false);
            return;
          }

          const { data: connData } = await supabase
            .from("google_calendar_credentials")
            .select("id, email, is_active, calendar_id, updated_at, needs_reconnect")
            .eq("workspace_id", workspaceId)
            .eq("is_active", true)
            .order("updated_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (connData && connectionStartTimeRef.current) {
            const updatedAt = new Date(connData.updated_at);
            const startTime = new Date(connectionStartTimeRef.current);

            if (updatedAt > startTime) {
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }

              setIsConnected(true);
              setConnectedEmail(connData.email);
              setActiveCredentialId(connData.id);
              setCurrentCalendarId(connData.calendar_id || "primary");
              setSelectedCalendar(connData.calendar_id || "primary");
              setIsConnecting(false);
              setTokenInvalid(false);

              await loadCalendars(connData.id);
              void bootstrapCalendarSync();
              toast.success("Google Calendar conectado com sucesso!");
            }
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Error connecting to calendar:", error);
      toast.error("Erro ao conectar com Google Calendar");
      setIsConnecting(false);
    }
  };

  const handleReconnect = async () => {
    setTokenInvalid(false);
    await handleConnect();
  };

  const handleDisconnect = async () => {
    if (!workspaceId) return;

    const targetCredentialId = propCredentialId || activeCredentialId;

    try {
      try {
        await supabase.functions.invoke("google-calendar-sync", {
          body: { action: "stop_watch", workspaceId, reason: "disconnect" },
        });
      } catch (watchError) {
        console.warn("Failed to stop Google Calendar watch before disconnect:", watchError);
      }

      const { error } = await supabase.functions.invoke("google-calendar-tools", {
        body: {
          action: "disconnect",
          workspaceId,
          params: targetCredentialId ? { credentialId: targetCredentialId } : undefined,
        },
      });

      if (error) {
        toast.error("Erro ao desconectar");
        return;
      }

      setIsConnected(false);
      setConnectedEmail(null);
      setActiveCredentialId(null);
      setTokenInvalid(false);
      toast.success("Google Calendar desconectado");
      onSaved?.();
    } catch (error) {
      console.error("Error disconnecting calendar:", error);
      toast.error("Erro ao desconectar");
    }
  };

  const enabledSelectionCount = selectedCalendars.size;
  const limitLabel = calendarLimit
    ? `${enabledSelectionCount} de ${calendarLimit.limit} agendas`
    : null;

  const selectionChanged = multiUiEnabled
    ? calendars.some((cal) => {
        const isSelected = selectedCalendars.has(cal.id);
        return isSelected;
      }) && enabledSelectionCount > 0
    : selectedCalendar !== currentCalendarId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-md max-h-[95vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="px-0 sm:px-0">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            <DialogTitle className="text-lg sm:text-xl">Google Calendar</DialogTitle>
          </div>
          <DialogDescription className="text-xs sm:text-sm">
            {multiUiEnabled
              ? "Selecione as agendas que deseja sincronizar com o FabChat"
              : "Conecte sua agenda do Google para permitir agendamentos automáticos via IA"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tokenInvalid && isConnected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 p-4 border border-yellow-200 dark:border-yellow-800">
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                    ⚠️ Token expirado ou inválido
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    A conexão com {connectedEmail} precisa ser renovada.
                  </p>
                </div>
              </div>

              <Button onClick={handleReconnect} disabled={isConnecting} className="w-full">
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Reconectando...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Reconectar Google Calendar
                  </>
                )}
              </Button>

              <Button variant="outline" onClick={handleDisconnect} className="w-full">
                Desconectar
              </Button>
            </div>
          ) : tokenInvalid ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 p-4 border border-yellow-200 dark:border-yellow-800">
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                    ⚠️ Token expirado ou inválido
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    A conexão precisa ser renovada. Clique em Reconectar abaixo.
                  </p>
                </div>
              </div>

              <Button onClick={handleReconnect} disabled={isConnecting} className="w-full">
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Reconectar Google Calendar
                  </>
                )}
              </Button>
            </div>
          ) : isConnected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-950/20 p-4">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900 dark:text-green-100">Conectado</p>
                  <p className="text-xs text-green-700 dark:text-green-300">{connectedEmail}</p>
                </div>
              </div>

              {isLoadingCalendars ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : calendars.length > 0 && (
                <div className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-medium">
                      {multiUiEnabled ? "Agendas para sincronizar:" : "Selecione a agenda:"}
                    </h4>
                    {multiUiEnabled && limitLabel && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{limitLabel}</span>
                    )}
                  </div>

                  {multiUiEnabled ? (
                    <div className="space-y-2">
                      {calendars.map((calendar) => (
                        <div key={calendar.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`gcal-${calendar.id}`}
                            checked={selectedCalendars.has(calendar.id)}
                            onCheckedChange={(checked) =>
                              toggleCalendarSelection(calendar.id, checked === true)
                            }
                          />
                          <Label htmlFor={`gcal-${calendar.id}`} className="text-sm cursor-pointer flex-1">
                            {calendar.summary}
                            {calendar.primary && (
                              <span className="ml-2 text-xs text-muted-foreground">(Principal)</span>
                            )}
                          </Label>
                        </div>
                      ))}
                      <Button
                        onClick={handleMultiSave}
                        size="sm"
                        className="w-full"
                        disabled={isSaving || enabledSelectionCount === 0}
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          "Salvar agendas selecionadas"
                        )}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <RadioGroup value={selectedCalendar} onValueChange={setSelectedCalendar}>
                        {calendars.map((calendar) => (
                          <div key={calendar.id} className="flex items-center space-x-2">
                            <RadioGroupItem value={calendar.id} id={calendar.id} />
                            <Label htmlFor={calendar.id} className="text-sm cursor-pointer flex-1">
                              {calendar.summary}
                              {calendar.primary && (
                                <span className="ml-2 text-xs text-muted-foreground">(Principal)</span>
                              )}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                      {selectionChanged && (
                        <Button
                          onClick={() => handleCalendarChange(selectedCalendar)}
                          size="sm"
                          className="w-full"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Salvando...
                            </>
                          ) : (
                            "Salvar agenda selecionada"
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="space-y-2 rounded-lg border p-4">
                <h4 className="text-sm font-medium">Como usar:</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Habilite o Calendar no agente desejado</li>
                  <li>A IA poderá consultar horários disponíveis</li>
                  <li>A IA poderá criar agendamentos automaticamente</li>
                  <li>Convites serão enviados por email</li>
                </ol>
              </div>

              <Button variant="outline" onClick={handleDisconnect} className="w-full">
                Desconectar
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2 rounded-lg border p-4">
                <h4 className="text-sm font-medium">Recursos:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Consultar disponibilidade em tempo real
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Criar agendamentos automaticamente
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Enviar convites por email
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Lembretes automáticos
                  </li>
                </ul>
              </div>

              <Button onClick={handleConnect} disabled={isConnecting} className="w-full">
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Conectar Google Calendar
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Você será redirecionado para autorizar o acesso à sua agenda
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
